import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import { alpha, keyframes } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, Paper, Button } from '@mui/material';
// routes
import { PATH_DASHBOARD, PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import Iconify from '../../../components/Iconify';
import useResponsive from 'src/hooks/useResponsive';
import { decrypt } from 'src/helpers/constants';

// ----------------------------------------------------------------------

const pulseRing = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(0, 171, 85, 0.7);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(0, 171, 85, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 171, 85, 0);
  }
`;

const MENU_OPTIONS = [
  // {
  //   label: 'Get Help',
  //   linkTo: 'event/exhibitors/help-desk',
  // },
  // {
  //   label: 'Event Homepage',
  //   linkTo: 'event',
  // },
];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const mdUp = useResponsive('up', 'md');

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);
  const [showGuide, setShowGuide] = useState(false);

  const activeEvent = localStorage.getItem('arsh:activeevent');

  const isSpeaker =
    user?.userType === 'speaker' ||
    user?.isSpeaker ||
    user?.is_speaker ||
    user?.role === 'speaker' ||
    user?.delegate_type?.toLowerCase()?.includes('speaker') ||
    user?.delegate_type_name_at_reg?.toLowerCase()?.includes('speaker');

  useEffect(() => {
    if (isSpeaker && !localStorage.getItem('arsh:speakerGuideDismissed')) {
      setShowGuide(true);
    }
  }, [isSpeaker]);

  const handleDismissGuide = () => {
    setShowGuide(false);
    localStorage.setItem('arsh:speakerGuideDismissed', 'true');
  };

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    if (showGuide) {
      handleDismissGuide();
    }
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout.', { variant: 'error' });
    }
  };

  return (
    <>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <Box
          sx={{
            borderRadius: '50%',
            display: 'inline-flex',
            ...(showGuide && {
              animation: `${pulseRing} 2s infinite`,
              border: '2px solid #00AB55',
              p: '2px',
            }),
          }}
        >
          <IconButtonAnimate
            onClick={handleOpen}
            sx={{
              p: 0,
              mr: mdUp ? 0 : 3,
              ...(open && {
                '&:before': {
                  zIndex: 1,
                  content: "''",
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  position: 'absolute',
                  bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
                },
              }),
            }}
          >
            <MyAvatar />
          </IconButtonAnimate>
        </Box>

        {showGuide && (
          <Paper
            elevation={12}
            sx={{
              position: 'absolute',
              top: 55,
              right: mdUp ? 0 : 8,
              zIndex: 9999,
              width: { xs: 260, sm: 280 },
              p: 2,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: '2px solid #00AB55',
              boxShadow: '0px 8px 24px rgba(0, 171, 85, 0.25)',
              '&:before': {
                content: '""',
                position: 'absolute',
                top: -8,
                right: mdUp ? 20 : 28,
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderBottom: '8px solid #00AB55',
              },
            }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Iconify icon="eva:bulb-fill" sx={{ color: '#00AB55', width: 22, height: 22 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Speaker Dashboard Guide
                </Typography>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
                Click your profile picture above to open the menu and access your <b>Speaker Dashboard</b> to manage your bio and slides!
              </Typography>
              <Button
                size="small"
                variant="contained"
                sx={{ alignSelf: 'flex-end', mt: 1, fontSize: '0.75rem', bgcolor: '#00AB55', '&:hover': { bgcolor: '#007B55' } }}
                onClick={handleDismissGuide}
              >
                Got it!
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" noWrap>
            {decrypt(user?.first_name)} {decrypt(user?.last_name)} {decrypt(user?.name)}
          </Typography>
          <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
            {isSpeaker
              ? 'Speaker'
              : user?.delegate_type_name_at_reg ||
                (user?.delegate_type && user?.delegate_type + ' delegate') ||
                user?.delegate_type ||
                'Delegate'}
          </Typography>
        </Box>

        {/* <Divider sx={{ borderStyle: 'dashed' }} /> */}

        <Stack sx={{ p: 1 }}>
          {isSpeaker && (
            <MenuItem
              to={`/${activeEvent}/speaker-dashboard`}
              component={RouterLink}
              onClick={handleClose}
            >
              Speaker Dashboard
            </MenuItem>
          )}
          {MENU_OPTIONS.map((option) => (
            <MenuItem key={option.label} to={option.linkTo} component={RouterLink} onClick={handleClose}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
